import {
  Button,
  FormErrorIcon,
  FormErrorMessage,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  useDisclosure,
} from '@chakra-ui/react';
import { colors } from '@/styles/theme/foundations/colors';
import {
  Eye as EyeIcon,
  EyeOff as EyeOffIcon,
  Lock as LockIcon,
  LogIn as LogInIcon,
  User as UserIcon,
} from 'react-feather';
import { useRouter } from 'next/navigation';
import { FormState, UseFormRegister } from 'react-hook-form';
import { AuthEmailLoginDto } from '@/generated/nabisNYWebAPI.schemas';
import { auth } from '@/modules/link';

export const FormErrorMessageWithIcon = ({ message }: { message?: string }) => {
  return message ? (
    <FormErrorMessage>
      <FormErrorIcon /> {`${message}`}
    </FormErrorMessage>
  ) : (
    <></>
  );
};

export const BackToLoginButton = ({ title }: { title?: string }) => {
  const router = useRouter();
  return <BackButton title={title || 'Return to login'} onClick={() => router.push(auth.login)} />;
};

export const BackButton = ({ title, onClick }: { title: string; onClick: () => void }) => {
  return (
    <Button
      fontWeight={600}
      bg={'transparent'}
      variant={'outline'}
      color={colors.text.base.dark}
      border={`1px solid ${colors.border.form.default.dark}`}
      _hover={{ backgroundColor: 'rgba(0,0,0,0.2)' }}
      onClick={onClick}
    >
      {title}
    </Button>
  );
};

export const SignInButton = ({ title, onClick }: { title?: string; onClick?: () => void }) => (
  <Button
    color={colors.text.black}
    role={'button'}
    display={'flex'}
    onClick={onClick}
    justifyContent={'space-between'}
    marginRight={'1rem'}
  >
    {title || 'Sign in'} &nbsp;
    <LogInIcon width={12} height={12} />
  </Button>
);

interface NabisLoginInputProps {
  register: UseFormRegister<AuthEmailLoginDto>;
  formState?: FormState<AuthEmailLoginDto>;
}

export const UsernameInput = ({ register }: NabisLoginInputProps) => {
  return (
    <InputGroup size="md" mt={'28px'}>
      <InputLeftElement pointerEvents="none" color="gray.300">
        <UserIcon aria-label="username-icon" color={colors.icon.default} width={16} height={16} />
      </InputLeftElement>
      <Input
        pr="4.5rem"
        errorBorderColor={colors.border.form.critical.dark}
        type={'email'}
        placeholder="Email"
        border={`1px solid ${colors.border.form.default.dark}`}
        borderRadius={4}
        {...register('email', { required: 'Username is required' })}
        color={colors.text.secondary.light}
      />
    </InputGroup>
  );
};

export const PasswordInput = ({ register }: NabisLoginInputProps) => {
  const { isOpen: isVisible, onToggle } = useDisclosure();

  return (
    <InputGroup size="md" mt={'8px'}>
      <InputLeftElement pointerEvents="none" color="gray.300">
        <LockIcon aria-label="password-icon" color={colors.icon.default} width={16} height={16} />
      </InputLeftElement>
      <Input
        id="password"
        pr="4.5rem"
        type={isVisible ? 'text' : 'password'}
        placeholder="Enter password"
        border={`1px solid ${colors.border.form.default.dark}`}
        borderRadius={4}
        color={colors.text.secondary.light}
        {...register('password', { required: 'Password is required' })}
      />
      <InputRightElement width="4.5rem">
        <IconButton
          aria-label="show-password"
          bg="transparent"
          color={colors.text.secondary.light}
          outline={'none'}
          h="1.75rem"
          size="sm"
          onClick={onToggle}
          icon={isVisible ? <EyeIcon width={16} height={16} /> : <EyeOffIcon width={16} height={16} />}
        />
      </InputRightElement>
    </InputGroup>
  );
};
